import React, { useContext, useEffect, useRef, useState } from 'react';

import DOMPurify from 'isomorphic-dompurify';
import ContentEditable from 'react-contenteditable';
import { useInView } from 'react-intersection-observer';
import { useToasts } from 'react-toast-notifications';

import EditConfirmIcon from '@components/common/EditConfirmIcon/EditConfirmIcon';
import EditIcon from '@components/common/EditIcon/EditIcon';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import ParticipationBadge from '@components/common/ParticipationBadge/ParticipationBadge';
import Text from '@components/common/Text/Text';
import PotContext from '@components/lpc/Context/Pot/PotContext';
import { personRoles } from '@models/PotModel/personRoles';
import { statusPO } from '@models/PotModel/statusPO';
import { potParticipantType, potPrivacyType } from '@propTypes/potTypes';
import potService from '@services/domain/Lpc/PotService';
import { formatFullDateToWord } from '@services/utils/DateService';
import { htmlToText } from '@services/utils/StringService';
import { getUserAvatarSrc } from '@services/utils/UserService';

import css from './Header.module.scss';

type HeaderProps = {
    title: string;
    eventDate?: string;
    potRef: string;
    personRole: string;
    privacy: potPrivacyType;
    setIsTitleVisible?: (boolean) => void;
    potStatus: string;
    isOffered: boolean;
    isFrozenAccount?: boolean;
    mobileSize?: boolean;
    hasImage?: boolean;
};

const Header = ({
    title,
    eventDate,
    potRef,
    personRole,
    setIsTitleVisible,
    potStatus,
    isOffered,
    isFrozenAccount,
    mobileSize,
    hasImage,
}: HeaderProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const { addToast } = useToasts();

    const contentEditableRef = useRef(null);
    const text = useRef(title);

    const potContext = useContext(PotContext);
    const { getUpdatedPot } = potContext;

    const [isDisabled, setIsDisabled] = useState<boolean>(false);

    const [participantsList, setParticipantsList] = useState<potParticipantType[]>([]);
    const [randomParticipants, setRandomParticipants] = useState<potParticipantType[]>([]);

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [titleRef, inView] = useInView({
        threshold: 1,
        initialInView: true,
    });

    const getWording = () => {
        if (personRole !== personRoles.BENEFICIARY) {
            if (isOffered) {
                return t('amount.cta.offered-pot');
            }

            if (potStatus === statusPO.CLOSED) {
                return t('amount.cta.closed-pot');
            }
        }

        return eventDate && formatFullDateToWord(eventDate);
    };

    const handleChange = (e) => {
        return (text.current = e.target.value);
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setIsEditing(false);
            onSubmit();
        }
    };

    const onSubmit = () => {
        setIsLoading(true);
        if (text.current.length < 2) {
            setIsLoading(false);
            setIsEditing(false);
            text.current = title;
        } else {
            potService
                .updatePotTitle(potRef, htmlToText(text.current))
                .then((res) => {
                    if (res.status === 200) {
                        getUpdatedPot(potRef);
                        setIsLoading(false);
                        setIsEditing(false);
                    }
                })
                .then(
                    htmlToText(text.current) !== title &&
                        addToast(t(`success.TITLE_UPDATED`), {
                            appearance: 'success',
                            autoDismiss: true,
                        }),
                )
                .finally(() => {
                    setIsDisabled(true);
                    setIsLoading(false);
                    setIsEditing(false);
                })
                .catch(() =>
                    addToast(t(`errors.DEFAULT_ERROR_MESSAGE`), {
                        appearance: 'error',
                        autoDismiss: true,
                    }),
                );
        }
    };

    const setCursorPosition = (element, position) => {
        const range = document.createRange();
        const selection = window?.getSelection();
        range.setStart(element.childNodes[0], position);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    };

    const onEdit = () => {
        setIsEditing(true);
    };

    useEffect(() => {
        if (isEditing && contentEditableRef.current) {
            // Set cursor position to the end of the content
            const textLength = contentEditableRef.current.innerText.length;
            setCursorPosition(contentEditableRef.current, textLength);
        }
    }, [isEditing]);

    useEffect(() => {
        setIsTitleVisible(inView);
    }, [inView, setIsTitleVisible]);

    useEffect(() => {
        const shuffled = participantsList
            .filter((participant) => !participant.hideName)
            .sort(() => 0.5 - Math.random())
            .slice(0, 4);

        setRandomParticipants(shuffled);
    }, [participantsList]);

    useEffect(() => {
        potService
            .getAllParticipants(potRef, 0, 50)
            .then((res) => setParticipantsList(res))
            .catch((err) => err);
    }, [potRef]);

    return (
        <>
            {personRole === personRoles.ORGANISER && !isOffered ? (
                <div className={css.wrapper__organiser}>
                    {isFrozenAccount && (
                        <Text variant={'caption_01'} color={'txt-alert'} customClass={css.frozen_header}>
                            <span>
                                <img
                                    className={css.kycHeader__icon}
                                    src="/front-static/icons/display/alert-triangle-outline.svg"
                                    alt="alert"
                                />
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            t(
                                                `header.frozen-account-${
                                                    personRole === personRoles.PARTICIPANT ? 'participant' : 'organizer'
                                                }`,
                                            ),
                                        ),
                                    }}
                                />
                            </span>
                        </Text>
                    )}
                    <div className={css.header}>
                        {mobileSize && (
                            <Text variant={'tagline'} color={'brand-primary'}>
                                {getWording()}
                            </Text>
                        )}
                        <h1
                            ref={titleRef}
                            className={css.text}
                            onMouseEnter={() => {
                                setIsDisabled(false);
                            }}
                        >
                            <ContentEditable
                                innerRef={contentEditableRef}
                                className={`${css.title} ${isEditing && css.title__editable}`}
                                style={{ outline: 'none' }}
                                html={text.current}
                                onChange={(e) => handleChange(e)}
                                onKeyDown={(e) => onKeyDown(e)}
                                onFocus={() => setIsEditing(true)}
                                disabled={isDisabled}
                            />
                            {!isEditing ? (
                                <EditIcon onEdit={onEdit} customBtnClass={css.title__edit_icon} />
                            ) : (
                                <EditConfirmIcon
                                    customContainer={css.title__edit_container}
                                    customIcon={css.title__edit_confirm_icon}
                                    customSpinner={css.title__edit_spinner}
                                    isLoading={isLoading}
                                    onClose={() => {
                                        setIsEditing(false);
                                    }}
                                    onConfirm={onSubmit}
                                />
                            )}
                        </h1>
                        {!mobileSize && (
                            <Text variant={'tagline'} color={'brand-primary'}>
                                {getWording()}
                            </Text>
                        )}
                    </div>
                </div>
            ) : (
                <div
                    className={`${css.wrapper__participant} ${!hasImage ? css.wrapper__participant__with_margin : ''}`}
                >
                    {isFrozenAccount && (
                        <Text variant={'caption_01'} color={'txt-alert'} customClass={css.frozen_header}>
                            <span>
                                <img
                                    className={css.kycHeader__icon}
                                    src="/front-static/icons/display/alert-triangle-outline.svg"
                                    alt="alert"
                                />
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            t(
                                                `header.frozen-account-${
                                                    personRole === personRoles.PARTICIPANT ? 'participant' : 'organizer'
                                                }`,
                                            ),
                                        ),
                                    }}
                                />
                            </span>
                        </Text>
                    )}
                    <div className={isFrozenAccount ? `${css.header__header_frozen}` : `${css.header}`}>
                        <div className={css.participant}>
                            {!!randomParticipants?.length &&
                                randomParticipants.map((item, i) => (
                                    <div key={item.personID} className={css['participant__avatar__' + i]}>
                                        <ParticipationBadge
                                            avatar={getUserAvatarSrc(item)}
                                            firstName={item.firstName}
                                            lastName={item.lastName}
                                        />
                                    </div>
                                ))}
                            <Text variant={'tagline'} color={'brand-primary'}>
                                {getWording()}
                            </Text>
                            <h1 ref={titleRef} className={css.title}>
                                {title}
                            </h1>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;
